import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Clock, X, Pin } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { Reminder } from './types'
import { fmtTime, shortDate } from './reminderTime'
import { getEntityLinks, iconFor, deepLinkFor } from './reminderPayload'
import { SnoozeMenu } from './SnoozeMenu'

/* 便签色：bg 用 edge 主色的半透明值，亮底≈原浅色、暗底自动变深（内联 style 写不了 dark:）。
 * deep 是落在这层半透明底上的**文字色**，写死深色 hex 时暗底上只有 ~2:1 对比，
 * 所以走 globals.css 的 tone token（:root/.dark 各一档）。 */
export const NOTE_COLORS: Record<string, { bg: string; edge: string; deep: string }> = {
  yellow: { bg: 'rgba(250, 204, 21, .18)', edge: '#FACC15', deep: 'hsl(var(--tone-amber))' },
  pink: { bg: 'rgba(244, 114, 182, .18)', edge: '#F472B6', deep: 'hsl(var(--tone-rose))' },
  green: { bg: 'rgba(74, 222, 128, .18)', edge: '#4ADE80', deep: 'hsl(var(--tone-green))' },
  blue: { bg: 'rgba(96, 165, 250, .18)', edge: '#60A5FA', deep: 'hsl(var(--tone-blue))' },
}

const CARD_W = 296

interface Props {
  rem: Reminder
  index: number
  tz: string
  overdue: boolean
  pinned: boolean
  pos: { x: number; y: number } | null
  onDone: () => void
  onSnoozeTo: (dueDate: string, dueTime: string | null) => void
  onDismiss: () => void
  onTogglePin: () => void
  onDragEnd: (x: number, y: number) => void
}

function clampToViewport(x: number, y: number): { x: number; y: number } {
  const vw = typeof window === 'undefined' ? 1280 : window.innerWidth
  const vh = typeof window === 'undefined' ? 800 : window.innerHeight
  return { x: Math.max(8, Math.min(x, vw - CARD_W - 8)), y: Math.max(8, Math.min(y, vh - 60)) }
}

export function StickyPopup({ rem, index, tz, overdue, pinned, pos, onDone, onSnoozeTo, onDismiss, onTogglePin, onDragEnd }: Props) {
  const { t } = useTranslation('reminders')
  const [menuOpen, setMenuOpen] = useState(false)
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null)
  const dragState = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const c = NOTE_COLORS[rem.color] ?? NOTE_COLORS.yellow
  const rot = pinned || pos || drag ? 0 : index % 2 === 0 ? -1.6 : 1.3
  const when = overdue ? shortDate(rem.dueDate) : rem.dueTime ? fmtTime(rem.dueTime) : t('today')
  const links = getEntityLinks(rem)

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    const onDown = (e: PointerEvent) => {
      const el = menuRef.current
      if (el && !el.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onDown)
    }
  }, [menuOpen])

  const cardRef = useRef<HTMLDivElement>(null)
  // 没拖过的卡不自己算坐标:留在 RemindersHost 的 column-reverse 流里,浏览器按
  // 真实高度堆。拖过一次(有 pos)或正在拖(drag)才切 fixed 坐标脱流。
  const cur = drag ?? pos

  const onTapePointerDown = useCallback((e: React.PointerEvent) => {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    // 流式布局下卡片坐标只有浏览器知道,起点必须实测。注意卡片带 rotate,
    // getBoundingClientRect 给的是**旋转后的外接矩形**(比实际大几 px),直接当
    // left/top 用会在按下瞬间跳。rotate 绕中心,所以按中心点反算未旋转的几何位置;
    // offsetHeight 本身就是未旋转的布局高度。同时按下即把 rot 归零,接手时零位移。
    const el = cardRef.current
    const r = el?.getBoundingClientRect()
    const origX = r ? r.left + r.width / 2 - CARD_W / 2 : 0
    const origY = r && el ? r.top + r.height / 2 - el.offsetHeight / 2 : 0
    dragState.current = { startX: e.clientX, startY: e.clientY, origX, origY }
    setDrag({ x: origX, y: origY })
  }, [])

  const onTapePointerMove = useCallback((e: React.PointerEvent) => {
    const ds = dragState.current
    if (!ds) return
    setDrag(clampToViewport(ds.origX + (e.clientX - ds.startX), ds.origY + (e.clientY - ds.startY)))
  }, [])

  const onTapePointerUp = useCallback((e: React.PointerEvent) => {
    const ds = dragState.current
    dragState.current = null
    if (ds) {
      const final = clampToViewport(ds.origX + (e.clientX - ds.startX), ds.origY + (e.clientY - ds.startY))
      setDrag(null)
      if (Math.abs(e.clientX - ds.startX) > 3 || Math.abs(e.clientY - ds.startY) > 3) onDragEnd(final.x, final.y)
    }
  }, [onDragEnd])

  const btnStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 5, height: 28, padding: '0 11px',
    background: 'rgba(255,255,255,.72)', border: '1px solid rgba(0,0,0,.08)', borderRadius: 8,
    fontSize: 12, fontWeight: 600, color: c.deep, cursor: 'pointer',
  }

  return (
    <div className="sticky-pop" ref={cardRef} style={{
      ...(cur
        ? { position: 'fixed' as const, left: cur.x, top: cur.y }
        : { position: 'relative' as const }),
      width: CARD_W, padding: '14px 16px',
      // 不透明的 --card 打底 + c.bg 半透明色层：只写 c.bg 的话整张便签是块玻璃，
      // 底下页面内容会透上来（实测列表行文字穿到便签正文上）。色层仍走半透明，
      // 亮底得浅黄、暗底得深黄褐，保留「内联 style 也能自适应主题」的原设计。
      background: `linear-gradient(${c.bg}, ${c.bg}), hsl(var(--card))`, // design-ok: 双层背景保证不透底，不是装饰
      borderRadius: 4, border: `1px solid ${c.edge}55`,
      boxShadow: '0 14px 24px -8px rgba(40,30,10,.28), 0 2px 4px rgba(40,30,10,.12)',
      transform: `rotate(${rot}deg)`, pointerEvents: 'auto', zIndex: pinned ? 1001 : 1000, touchAction: 'none',
    }}>
      <div onPointerDown={onTapePointerDown} onPointerMove={onTapePointerMove} onPointerUp={onTapePointerUp}
        style={{
          position: 'absolute', top: -11, left: '50%', width: 86, height: 22,
          transform: 'translateX(-50%) rotate(-2deg)', background: 'rgba(255,255,255,.55)',
          border: '1px solid rgba(0,0,0,.05)', boxShadow: '0 1px 2px rgba(0,0,0,.06)', cursor: 'grab',
        }} />

      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 2 }}>
        <button onClick={onTogglePin} title={pinned ? t('unpin') : t('pin')} style={{
          width: 24, height: 24, display: 'grid', placeItems: 'center', background: 'transparent',
          border: 'none', color: c.deep, opacity: pinned ? 1 : 0.5, cursor: 'pointer', borderRadius: 6,
        }}><Pin size={14} fill={pinned ? c.deep : 'none'} /></button>
        <button onClick={onDismiss} title={t('dismiss')} style={{
          width: 24, height: 24, display: 'grid', placeItems: 'center', background: 'transparent',
          border: 'none', color: c.deep, opacity: 0.55, cursor: 'pointer', borderRadius: 6,
        }}><X size={14} /></button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6, paddingRight: 52 }}>
        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.4, color: c.deep, opacity: 0.75 }}>{t('reminderKicker')}</span>
        <span style={{
          fontSize: 10.5, fontWeight: 700, color: overdue ? 'hsl(var(--tone-red))' : c.deep,
          background: overdue ? 'rgba(239, 68, 68, .18)' : 'rgba(255,255,255,.6)', padding: '2px 7px', borderRadius: 99,
        }}>{overdue ? `Overdue · ${when}` : when}</span>
      </div>

      <div style={{ fontFamily: "'Caveat', cursive", fontSize: 23, lineHeight: 1.2, color: 'hsl(var(--note-ink))', paddingRight: 10 }}>
        {rem.text}
      </div>

      {links.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {links.map((lk) => {
            const Icon = iconFor(lk.type)
            return (
              <Link key={`${lk.type}-${lk.id}`} to={deepLinkFor(lk)} title={lk.label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 230, height: 22, padding: '0 8px',
                background: 'rgba(255,255,255,.7)', borderRadius: 99, fontSize: 11, fontWeight: 600,
                color: c.deep, textDecoration: 'none',
              }}>
                <Icon className="h-3 w-3 shrink-0" />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lk.label}</span>
              </Link>
            )
          })}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <button style={btnStyle} onClick={onDone}><Check size={13} /> {t('done')}</button>
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button style={btnStyle} onClick={() => setMenuOpen((v) => !v)}><Clock size={13} /> {t('snooze')}</button>
          {menuOpen && (
            // 刻意不用 Radix Popover:贴纸自己 z-index 1000/1001,Radix 的 portal 层会被压在下面
            <div style={{
              // 右对齐:贴纸被 clampToViewport 钉在视口右缘(vw - CARD_W - 8),菜单从
              // 240px 起就比卡片剩余右侧空间宽,left:0 会把右缘顶出屏幕且没有滚动条可救
              // (实测 Move 按钮被裁)。锚右缘往左展开才永远在视口内。
              position: 'absolute', bottom: 34, right: 0, width: 240, background: 'hsl(var(--popover))', borderRadius: 10,
              border: '1px solid hsl(var(--border))', boxShadow: '0 10px 28px -6px rgba(0,0,0,.25)', padding: 4, zIndex: 5,
            }}>
              <SnoozeMenu tz={tz} currentTime={rem.dueTime}
                onPick={(d, tm) => { setMenuOpen(false); onSnoozeTo(d, tm) }} />
            </div>
          )}
        </div>
      </div>

      <div style={{
        position: 'absolute', right: 0, bottom: 0, width: 20, height: 20,
        background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,.07) 50%)', borderBottomRightRadius: 4, // design-ok: 便签折角
      }} />
    </div>
  )
}
